import React, { useEffect, useRef, useCallback } from 'react';
import unreadMessageIcon from 'app/assets/icons/unread-message.svg';
import { logError } from 'app/services/logger';

import useUnreadMessageCount from './useUnreadMessageCount';

export function createUpdateFaviconAndTitle(
  unreadCountRef: React.MutableRefObject<number>,
  unreadMessageIcon: string,
  logError: (message: string, error: unknown) => void
) {
  return () => {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const context = canvas.getContext('2d');
    if (!context) return undefined;

    const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement | null;
    if (!favicon) return undefined;

    const originalFaviconHref = favicon.href;
    const originalTitle = document.title;

    const badge = new Image();
    badge.crossOrigin = 'anonymous';
    badge.src = unreadMessageIcon;
    let intervalId: NodeJS.Timer;

    badge.onload = () => {
      const originalIcon = new Image();
      originalIcon.crossOrigin = 'anonymous';
      originalIcon.src = originalFaviconHref;
      originalIcon.onload = () => {
        const aspectRatio = originalIcon.width / originalIcon.height;
        let drawWidth = 32;
        let drawHeight = 32;

        if (aspectRatio > 1) {
          drawHeight = 32 / aspectRatio;
        } else {
          drawWidth = 32 * aspectRatio;
        }

        const offsetX = (32 - drawWidth) / 2;
        const offsetY = (32 - drawHeight) / 2;

        context.drawImage(originalIcon, offsetX, offsetY, drawWidth, drawHeight);
        context.drawImage(badge, 20, 0, 12, 12);

        let showingBadge = true;
        intervalId = setInterval(() => {
          if (unreadCountRef.current === 0) {
            if (intervalId) clearInterval(intervalId);
            favicon.href = originalFaviconHref;
            document.title = originalTitle;
          } else {
            try {
              document.title = showingBadge ? 'New message' : originalTitle;
              favicon.href = showingBadge ? canvas.toDataURL('image/png') : originalFaviconHref;
              showingBadge = !showingBadge;
            } catch (error) {
              document.title = originalTitle;
              favicon.href = originalFaviconHref;
              if (intervalId) clearInterval(intervalId);
              logError('Failed to update favicon and title.', error);
            }
          }
        }, 1000);
      };
    };

    return () => {
      if (intervalId) clearInterval(intervalId);
      favicon.href = originalFaviconHref;
      document.title = originalTitle;
    };
  };
}

export default function useBrowserNotification() {
  const unreadMessageCount = useUnreadMessageCount();
  const unreadCountRef = useRef(unreadMessageCount);
  unreadCountRef.current = unreadMessageCount;

  const updateFaviconAndTitle = useCallback(
    () => createUpdateFaviconAndTitle(unreadCountRef, unreadMessageIcon, logError),
    [unreadCountRef]
  );

  useEffect(() => {
    const cleanup = updateFaviconAndTitle();
    return () => {
      if (cleanup) cleanup();
    };
  }, [unreadMessageCount, updateFaviconAndTitle]);
}
